import { HttpClient, HttpParams } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { TokenService } from '@utilities/services/token.service';
import { Observable, map, catchError, of, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ModalService } from '../../shared/services/modal.service';
import { LoginRequest } from '../interfaces/login-request.interface';
import { LoginResponse } from '../interfaces/login-response.interface';
import { SharedLoginResponse } from '../interfaces/shared-login-response.interface';
import { RegisterRequest } from '../interfaces/register-request.interface';
import { ValidateMemberInfoResponse } from '../interfaces/validate-member-info-response.interface';
import { ValidateMemberInfoParams } from '../interfaces/validate-member-info-params.interface';
import { GetProgramResponse } from '../interfaces/get-program-response.interface';
import { GetProfileResponse } from '../interfaces/get-profile-response.interface';
import { LoginMemberRequest } from '../interfaces/login-member-request.interface';
import { ResetPasswordRequest } from '../interfaces/reset-password-request.interface';

const API_URL = environment.apiUrl;

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private tokenService = inject(TokenService);
  private modalService = inject(ModalService);
  private _authResp = signal<SharedLoginResponse | null>(null);
  authResp = computed(() => this._authResp());

  #authUrl = `${API_URL}/auth`;

  private _programsCache = signal<GetProgramResponse[] | null>(null);
  private _profilesCache = new Map<string, GetProfileResponse[]>();

  isLoggedIn(): boolean {
    const token = this.tokenService.getToken();

    if (!token) return false;

    const isTokenExpired = this.tokenService.isTokenExpired();

    if (isTokenExpired) {
      this.logout();
      return false;
    }

    return true;
  }

  login(request: LoginRequest): Observable<boolean> {
    return this.http.post<LoginResponse>(`${API_URL}/login`, request).pipe(
      map((response) => this.handleAuthSuccess(response)),
      catchError((err) => {
        this.modalService.showErrorModal(err);

        return of(false);
      }),
    );
  }

  loginMember(request: LoginMemberRequest): Observable<boolean> {
    return this.http
      .post<LoginResponse>(`${this.#authUrl}/login-member`, request)
      .pipe(
        map((response) => this.handleAuthSuccess(response)),
        catchError((err) => {
          this.modalService.showErrorModal(err);

          return of(false);
        }),
      );
  }

  recoverPassword(email: string): Observable<boolean> {
    return this.http
      .post<boolean>(`${this.#authUrl}/forgot-password`, { email })
      .pipe(
        map(() => true),
        catchError((err) => {
          this.modalService.showErrorModal(err);
          return of(false);
        }),
      );
  }

  resetPassword(request: ResetPasswordRequest): Observable<boolean> {
    return this.http
      .patch<boolean>(`${this.#authUrl}/reset-password`, request)
      .pipe(
        map(() => true),
        catchError((err) => {
          this.modalService.showErrorModal(err);
          return of(false);
        }),
      );
  }

  getPrograms(): Observable<GetProgramResponse[]> {
    if (this._programsCache()) {
      return of(this._programsCache()!);
    }

    return this.http
      .get<GetProgramResponse[]>(`${this.#authUrl}/programs`)
      .pipe(tap((programs) => this._programsCache.set(programs)));
  }

  getProfiles(programId: string): Observable<GetProfileResponse[]> {
    if (this._profilesCache.get(programId)) {
      return of(this._profilesCache.get(programId)!);
    }
    const params = new HttpParams().set('program', programId);

    return this.http
      .get<GetProfileResponse[]>(`${this.#authUrl}/profiles`, { params })
      .pipe(tap((profiles) => this._profilesCache.set(programId, profiles)));
  }

  validateMemberInfo(
    params: ValidateMemberInfoParams,
  ): Observable<ValidateMemberInfoResponse | null> {
    // console.log(params);

    const httpParams = new HttpParams()
      .set('gpid', params.gpid)
      .set('program', params.program)
      .set('profile', params.profile);
    return this.http
      .get<ValidateMemberInfoResponse | null>(`${this.#authUrl}/member-info`, {
        params: httpParams,
      })
      .pipe(
        catchError((err) => {
          this.modalService.showErrorModal(err);
          return of(null);
        }),
      );
  }

  register(request: RegisterRequest): Observable<boolean> {
    return this.http
      .post<boolean>(`${this.#authUrl}/register-member`, request)
      .pipe(
        map((response) => response),
        catchError((err) => {
          this.modalService.showErrorModal(err);
          return of(false);
        }),
      );
  }

  logout(): void {
    this.tokenService.removeTokenKeys();
  }

  private handleAuthSuccess({ accessToken }: LoginResponse): boolean {
    this.tokenService.saveToken(accessToken);
    this.tokenService.saveTokenExpiration();

    return true;
  }
}
